// src/hooks/useCardGrid.js
import { useState, useCallback, useMemo, useEffect } from 'react';
import { Logger } from '../utils/logger';

const logger = new Logger('useCardGrid');

export default function useCardGrid() {
  // Detect screen size for responsive layout
  const [isTablet, setIsTablet] = useState(window.innerWidth <= 1200);

  useEffect(() => {
    const handleResize = () => {
      setIsTablet(window.innerWidth <= 1200);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Track which card is in each position
  const [gridPositions, setGridPositions] = useState({
    // Column A positions
    A1: null, A2: null, A3: null, A4: null, A5: null,
    // Column B positions
    B1: null, B2: null, B3: null, B4: null, B5: null,
    // Column C positions
    C1: null, C2: null, C3: null, C4: null, C5: null
  });

  // Track all cards by ID with their data
  const [cards, setCards] = useState({});

  // Base Z-index and positioning constants
  const Z_INDEX_BASE = 10;
  const HEADER_HEIGHT = 80; // Increased to create more space between stacked headers

  // Responsive column positions
  const COLUMN_POSITIONS = useMemo(() => {
    if (isTablet) {
      // Tablet: 2 columns side by side
      const viewportWidth = window.innerWidth;
      const cardWidth = 500;
      const totalWidth = cardWidth * 2;
      const spacing = 20;
      const leftMargin = (viewportWidth - totalWidth - spacing) / 2;
      return {
        A: { x: leftMargin },
        B: { x: leftMargin + cardWidth + spacing },
        C: { x: leftMargin + cardWidth + spacing }  // Map C to B
      };
    }
    // Desktop: 3 columns with fixed positioning
    return {
      A: { x: 20 },
      B: { x: 560 },
      C: { x: 1100 }
    };
  }, [isTablet]);

  const INITIAL_Y = 80; // Start position at top of viewport (below header)

  // Calculate physical position based on grid position
  const getPhysicalPosition = useCallback((gridPosition) => {
    const column = gridPosition.charAt(0);
    const row = parseInt(gridPosition.charAt(1), 10);

    return {
      x: COLUMN_POSITIONS[column].x,
      y: INITIAL_Y + ((row - 1) * HEADER_HEIGHT), // Position 1 at top (y=80), stack downward
      zIndex: Z_INDEX_BASE + (10 * row) // Position 5 has highest Z-index
    };
  }, [COLUMN_POSITIONS]); // Fixed: Added COLUMN_POSITIONS to dependency array
  
  // Count total cards in the grid
  const getTotalCardCount = useCallback(() => {
    return Object.values(gridPositions).filter(id => id !== null).length;
  }, [gridPositions]);
  
  // Get column card counts
  const getColumnCardCounts = useCallback(() => {
    const counts = { A: 0, B: 0, C: 0 };
    
    Object.entries(gridPositions).forEach(([pos, id]) => {
      if (id !== null) {
        const column = pos.charAt(0);
        counts[column]++;
      }
    });
    
    return counts;
  }, [gridPositions]);
  
  // Get column for new card - always returns position 5 in a suitable column
  const getNextAvailableColumn = useCallback(() => {
    const columnCounts = getColumnCardCounts();

    // On tablets, use columns A and B
    if (isTablet) {
      return columnCounts.A <= columnCounts.B ? 'A' : 'B';
    }

    // Desktop: Find column with the least cards
    let targetColumn = 'A';
    let minCount = columnCounts.A;

    if (columnCounts.B < minCount) {
      targetColumn = 'B';
      minCount = columnCounts.B;
    }

    if (columnCounts.C < minCount) {
      targetColumn = 'C';
    }

    return targetColumn;
  }, [getColumnCardCounts, isTablet]);
  
  // Add a new card to the grid
  const addCard = useCallback((cardId, cardData, cardType) => {
    // Check if we already have 15 cards
    const totalCards = getTotalCardCount();
    if (totalCards >= 15) {
      logger.warn('Maximum card limit (15) reached. Cannot add more cards.');
      return null;
    }

    // Get the column with the least cards
    const targetColumn = getNextAvailableColumn();

    // Find the next available position in the column (1→2→3→4→5)
    let nextPosition = null;
    for (let i = 1; i <= 5; i++) {
      if (gridPositions[`${targetColumn}${i}`] === null) {
        nextPosition = i;
        break;
      }
    }

    // Calculate all the necessary updates in one go
    // 1. Create a copy of the current grid positions
    const newGridPositions = { ...gridPositions };

    // 2. Create a copy of the current cards
    const updatedCards = { ...cards };

    if (nextPosition !== null) {
      // Column has space - add card to next available position
      const newCard = {
        id: cardId,
        data: cardData,
        type: cardType,
        gridPosition: `${targetColumn}${nextPosition}`,
        ...getPhysicalPosition(`${targetColumn}${nextPosition}`)
      };

      newGridPositions[`${targetColumn}${nextPosition}`] = cardId;
      updatedCards[cardId] = newCard;
    } else {
      // Column is full - delete position 1, shift all down, add at position 5
      // 3. Remove the card at position 1
      const cardToRemove = gridPositions[`${targetColumn}1`];
      if (cardToRemove) {
        delete updatedCards[cardToRemove];
      }

      // 4. Shift all existing cards DOWN (2→1, 3→2, 4→3, 5→4)
      for (let i = 1; i < 5; i++) {
        const cardToShift = gridPositions[`${targetColumn}${i+1}`];
        newGridPositions[`${targetColumn}${i}`] = cardToShift;

        // Update that card's position info if it exists
        if (cardToShift) {
          updatedCards[cardToShift] = {
            ...updatedCards[cardToShift],
            gridPosition: `${targetColumn}${i}`,
            ...getPhysicalPosition(`${targetColumn}${i}`)
          };
        }
      }

      // 5. Create new card entry that will go in position 5
      const newCard = {
        id: cardId,
        data: cardData,
        type: cardType,
        gridPosition: `${targetColumn}5`,
        ...getPhysicalPosition(`${targetColumn}5`)
      };

      // 6. Place new card in position 5
      newGridPositions[`${targetColumn}5`] = cardId;
      updatedCards[cardId] = newCard;
    }

    // 7. Apply all updates at once
    setGridPositions(newGridPositions);
    setCards(updatedCards);

    return cardId;
  }, [getNextAvailableColumn, getPhysicalPosition, getTotalCardCount, gridPositions, cards]);
  
  // Move a card to the front (highest occupied position) when clicked
  const moveCardToFront = useCallback((cardId) => {
    // Find current position of card
    const card = cards[cardId];
    if (!card) return;

    const currentPosition = card.gridPosition;
    const column = currentPosition.charAt(0);
    const row = parseInt(currentPosition.charAt(1), 10);

    // Find the highest occupied position in this column
    let highestPosition = 1;
    for (let i = 5; i >= 1; i--) {
      if (gridPositions[`${column}${i}`] !== null) {
        highestPosition = i;
        break;
      }
    }

    // If already at the highest position, do nothing
    if (row === highestPosition) return;

    // Calculate all the necessary updates in one go
    // 1. Create a copy of the current grid positions
    const newGridPositions = { ...gridPositions };

    // 2. Create a copy of the current cards
    const updatedCards = { ...cards };

    // 3. Remove card from current position
    newGridPositions[currentPosition] = null;

    // 4. Move all cards between current position and highest position DOWN
    for (let i = row; i < highestPosition; i++) {
      const currentPos = `${column}${i}`;
      const nextPos = `${column}${i+1}`;

      // Move card from position i+1 to position i
      if (gridPositions[nextPos] && gridPositions[nextPos] !== cardId) {
        const cardToMove = gridPositions[nextPos];
        newGridPositions[currentPos] = cardToMove;
        newGridPositions[nextPos] = null;

        // Update that card's position info
        updatedCards[cardToMove] = {
          ...updatedCards[cardToMove],
          gridPosition: currentPos,
          ...getPhysicalPosition(currentPos)
        };
      }
    }

    // 5. Put clicked card in highest position
    newGridPositions[`${column}${highestPosition}`] = cardId;

    // 6. Update the clicked card's position info
    updatedCards[cardId] = {
      ...updatedCards[cardId],
      gridPosition: `${column}${highestPosition}`,
      ...getPhysicalPosition(`${column}${highestPosition}`)
    };

    // 7. Apply all updates at once
    setGridPositions(newGridPositions);
    setCards(updatedCards);
  }, [cards, gridPositions, getPhysicalPosition]);
  
  // Determine which column a position belongs to based on x coordinate
  const getColumnFromPosition = useCallback((x) => {
    // Get the screen width to help with dynamic boundaries
    const screenWidth = window.innerWidth;

    // Card width for center calculation
    const cardWidth = 500;

    // Convert left edge position to center position for better column detection
    const cardCenterX = x + (cardWidth / 2);

    // On tablets, use 2 columns (A and B)
    if (isTablet) {
      const midpoint = screenWidth / 2;
      return cardCenterX < midpoint ? 'A' : 'B';
    }

    // Desktop: 3 columns
    const columnPositions = {
      A: COLUMN_POSITIONS.A.x,
      B: COLUMN_POSITIONS.B.x,
      C: COLUMN_POSITIONS.C.x
    };

    // Calculate the center of each column
    const columnA_center = columnPositions.A + (cardWidth / 2);
    const columnB_center = columnPositions.B + (cardWidth / 2);
    const columnC_center = columnPositions.C + (cardWidth / 2);

    // Calculate boundaries between columns (halfway between column centers)
    const AB_boundary = (columnA_center + columnB_center) / 2;
    const BC_boundary = (columnB_center + columnC_center) / 2;

    // Determine target column based on card center position
    let targetColumn;

    if (cardCenterX < AB_boundary) {
      targetColumn = 'A';
    } else if (cardCenterX < BC_boundary) {
      targetColumn = 'B';
    } else {
      targetColumn = 'C';
    }

    return targetColumn;
  }, [COLUMN_POSITIONS, isTablet]);
  
  // Move a card to a different column after dragging
  const moveCardToColumn = useCallback((cardId, newPosition) => {
    try {
      logger.debug('[MOVE] moveCardToColumn called with:', { cardId, newPosition });

      // Find the card
      const card = cards[cardId];
      if (!card) {
        logger.warn('[MOVE] Card not found:', cardId);
        return;
      }

      const currentPosition = card.gridPosition;
      const sourceColumn = currentPosition.charAt(0);
      const currentRow = parseInt(currentPosition.charAt(1), 10);

      // Determine which column we're moving to
      const targetColumn = getColumnFromPosition(newPosition.x);
      logger.debug('[MOVE] Target column determined:', { sourceColumn, targetColumn, x: newPosition.x });

      // If same column, just snap back to position
      if (targetColumn === sourceColumn) {
        logger.debug('[MOVE] Same column, snapping back');
        setCards(prevCards => ({
          ...prevCards,
          [cardId]: {
            ...prevCards[cardId],
            ...getPhysicalPosition(currentPosition)
          }
        }));
        return;
      }

      // Find next available position in target column (first available from top)
      let nextPosition = null;
      for (let i = 1; i <= 5; i++) {
        if (gridPositions[`${targetColumn}${i}`] === null) {
          nextPosition = i;
          break;
        }
      }
      logger.debug('[MOVE] Next available position in target column:', nextPosition);

      // Check if target column is full
      if (nextPosition === null) {
        logger.warn('[MOVE] Target column is full, snapping back');
        // Target column is full, snap back to original position
        setCards(prevCards => ({
          ...prevCards,
          [cardId]: {
            ...prevCards[cardId],
            ...getPhysicalPosition(currentPosition)
          }
        }));
        return;
      }

      // The column has space, so we can proceed with the move
      logger.debug('[MOVE] Proceeding with move to', targetColumn, 'position', nextPosition);

      // Calculate all the necessary updates in one go
      // 1. Create a copy of the current grid positions
      const newGridPositions = { ...gridPositions };
      const updatedCards = { ...cards };

      // 2. Find the highest occupied position in source column
      let highestInSource = 1;
      for (let i = 5; i >= 1; i--) {
        if (gridPositions[`${sourceColumn}${i}`] !== null) {
          highestInSource = i;
          break;
        }
      }

      // 3. Remove card from current position in source column
      newGridPositions[currentPosition] = null;

      // 4. Shift remaining cards in source column down to fill the gap
      // (cards at higher positions move to lower positions - same as removeCard logic)
      for (let i = currentRow; i < highestInSource; i++) {
        const currentPos = `${sourceColumn}${i}`;
        const nextPos = `${sourceColumn}${i+1}`;

        const cardToShift = gridPositions[nextPos];
        if (cardToShift) {
          newGridPositions[currentPos] = cardToShift;
          newGridPositions[nextPos] = null;

          updatedCards[cardToShift] = {
            ...updatedCards[cardToShift],
            gridPosition: currentPos,
            ...getPhysicalPosition(currentPos)
          };
        }
      }

      // 4. Place the dragged card in next available position in target column
      newGridPositions[`${targetColumn}${nextPosition}`] = cardId;

      updatedCards[cardId] = {
        ...updatedCards[cardId],
        gridPosition: `${targetColumn}${nextPosition}`,
        ...getPhysicalPosition(`${targetColumn}${nextPosition}`)
      };

      // 5. Apply all updates at once
      setGridPositions(newGridPositions);
      setCards(updatedCards);
      logger.debug('[MOVE] Move completed successfully');
    } catch (error) {
      logger.error('[MOVE] Error in moveCardToColumn:', error);
    }
  }, [cards, gridPositions, getPhysicalPosition, getColumnFromPosition]);
  
  // Clear all cards from the grid
  const clearGrid = useCallback(() => {
    setCards({});
    setGridPositions({
      A1: null, A2: null, A3: null, A4: null, A5: null,
      B1: null, B2: null, B3: null, B4: null, B5: null,
      C1: null, C2: null, C3: null, C4: null, C5: null
    });
  }, []);

  // Restore cards from a saved snapshot — bypasses the placement algorithm
  // snapshots: [{ id, type, data, gridPosition }]
  const restoreCards = useCallback((snapshots) => {
    const newCards = {};
    const newGridPositions = {
      A1: null, A2: null, A3: null, A4: null, A5: null,
      B1: null, B2: null, B3: null, B4: null, B5: null,
      C1: null, C2: null, C3: null, C4: null, C5: null
    };
    snapshots.forEach(snap => {
      if (!snap.gridPosition || !newGridPositions.hasOwnProperty(snap.gridPosition)) return;
      newCards[snap.id] = {
        id: snap.id,
        data: snap.data,
        type: snap.type,
        gridPosition: snap.gridPosition,
        ...getPhysicalPosition(snap.gridPosition)
      };
      newGridPositions[snap.gridPosition] = snap.id;
    });
    setCards(newCards);
    setGridPositions(newGridPositions);
  }, [getPhysicalPosition]);

  // Remove a card from the grid
  const removeCard = useCallback((cardId) => {
    // Find current position of card
    const card = cards[cardId];
    if (!card) return;

    const currentPosition = card.gridPosition;
    const column = currentPosition.charAt(0);
    const row = parseInt(currentPosition.charAt(1), 10);

    // Find the highest occupied position in this column
    let highestPosition = 1;
    for (let i = 5; i >= 1; i--) {
      if (gridPositions[`${column}${i}`] !== null) {
        highestPosition = i;
        break;
      }
    }

    // Calculate all the necessary updates in one go
    // 1. Create a copy of the current grid positions
    const newGridPositions = { ...gridPositions };

    // 2. Create a copy of the current cards
    const updatedCards = { ...cards };

    // 3. Remove card from current position
    newGridPositions[currentPosition] = null;
    delete updatedCards[cardId];

    // 4. Shift cards down to fill the gap (if we remove position 3, move 4→3, 5→4)
    for (let i = row; i < highestPosition; i++) {
      const currentPos = `${column}${i}`;
      const nextPos = `${column}${i+1}`;

      const cardToShift = gridPositions[nextPos];
      if (cardToShift) {
        newGridPositions[currentPos] = cardToShift;
        newGridPositions[nextPos] = null;

        // Update the card's position info
        updatedCards[cardToShift] = {
          ...updatedCards[cardToShift],
          gridPosition: currentPos,
          ...getPhysicalPosition(currentPos)
        };
      }
    }

    // 5. Apply all updates at once
    setGridPositions(newGridPositions);
    setCards(updatedCards);
  }, [cards, gridPositions, getPhysicalPosition]);
  
  return {
    cards,
    gridPositions,
    addCard,
    moveCardToFront,
    moveCardToColumn,
    removeCard,
    clearGrid,
    restoreCards,
    getPhysicalPosition,
    getTotalCardCount
  };
}
